import React, { useState } from "react";
import PropTypes from "prop-types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Pencil, Loader2 } from "lucide-react";
import warrantyApi from "@/api/warrantyApi";
import { WARRANTY_RETURN_CONDITIONS } from "@/lib/warrantyUtils";

const EditWarrantyClaimForm = ({ claim, onOpenChange, onDone }) => {
  const queryClient = useQueryClient();

  const itemDescription =
    claim?.item_description || claim?.product?.name || "";
  const serialNumber = claim?.serial_number || "";
  const [returnCondition, setReturnCondition] = useState(() => {
    if (typeof claim?.return_condition === "object" && claim?.return_condition !== null) {
      return claim.return_condition.value || "defective";
    }
    return claim?.return_condition || "defective";
  });
  const [defectDescription, setDefectDescription] = useState(
    claim?.defect_description || "",
  );
  const resolutionNotes = claim?.resolution_notes || claim?.notes || "";
  const warrantyStartDate = claim?.warranty_start_date
    ? claim.warranty_start_date.split("T")[0]
    : "";
  const warrantyEndDate = claim?.warranty_end_date
    ? claim.warranty_end_date.split("T")[0]
    : "";

  const updateMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        item_description: itemDescription?.trim() || undefined,
        defect_description: defectDescription.trim(),
        return_condition: returnCondition,
        serial_number: serialNumber?.trim() ? serialNumber.trim() : null,
        warranty_start_date: warrantyStartDate || null,
        warranty_end_date: warrantyEndDate || null,
        ...(resolutionNotes ? { resolution_notes: resolutionNotes } : {}),
      };
      const res = await warrantyApi.update(claim.id, payload);
      return res.data;
    },
    onSuccess: () => {
      toast.success("Warranty claim updated successfully");
      queryClient.invalidateQueries({ queryKey: ["warrantyClaims"] });
      if (claim?.id) {
        queryClient.invalidateQueries({ queryKey: ["warrantyClaim", String(claim.id)] });
      }
      onOpenChange(false);
      if (onDone) onDone();
    },
    onError: (error) => {
      const msg = error.response?.data?.message || "Failed to update warranty claim";
      toast.error(msg);
      queryClient.invalidateQueries({ queryKey: ["warrantyClaims"] });
      if (claim?.id) {
        queryClient.invalidateQueries({ queryKey: ["warrantyClaim", String(claim.id)] });
      }
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!defectDescription.trim()) {
      toast.error("Please enter a customer defect description");
      return;
    }
    updateMutation.mutate();
  };

  return (
    <>
      <DialogHeader>
        <div className="flex items-center gap-2">
          <div className="p-2 bg-primary/10 rounded-lg text-primary">
            <Pencil className="h-5 w-5" />
          </div>
          <div>
            <DialogTitle>Edit Warranty Claim</DialogTitle>
            <DialogDescription>
              Update claim details and hardware information for claim #{claim.claim_number || claim.id}
            </DialogDescription>
          </div>
        </div>
      </DialogHeader>

      {/* Claim context summary */}
      <div className="rounded-lg border bg-muted/40 p-3 text-xs space-y-1 my-1">
        <div className="flex items-center justify-between">
          <span className="font-medium text-foreground">
            {claim.product?.name || claim.item_description || "Product"}
          </span>
          {claim.claim_number && (
            <Badge variant="outline" className="font-mono text-[11px]">
              #{claim.claim_number}
            </Badge>
          )}
        </div>
        {claim.customer && (
          <p className="text-muted-foreground pt-0.5">
            Customer:{" "}
            <strong className="text-foreground">
              {claim.customer.customer_name ||
                claim.customer.full_name ||
                claim.customer.name ||
                "Customer"}
            </strong>
          </p>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 py-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="edit_item_description">Item Description</Label>
              <span className="text-[10px] text-muted-foreground">View-only</span>
            </div>
            <Input
              id="edit_item_description"
              value={itemDescription}
              disabled
              className="bg-muted/50 cursor-not-allowed"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="edit_serial_number">Serial Number</Label>
              <span className="text-[10px] text-muted-foreground">View-only</span>
            </div>
            <Input
              id="edit_serial_number"
              value={serialNumber || "N/A"}
              disabled
              className="bg-muted/50 cursor-not-allowed font-mono"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="edit_warranty_start_date">Warranty Start Date</Label>
              <span className="text-[10px] text-muted-foreground">View-only</span>
            </div>
            <Input
              id="edit_warranty_start_date"
              type="date"
              value={warrantyStartDate}
              disabled
              className="bg-muted/50 cursor-not-allowed"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="edit_warranty_end_date">Warranty End Date</Label>
              <span className="text-[10px] text-muted-foreground">View-only</span>
            </div>
            <Input
              id="edit_warranty_end_date"
              type="date"
              value={warrantyEndDate}
              disabled
              className="bg-muted/50 cursor-not-allowed"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="edit_return_condition">Initial Return Condition</Label>
          <Select
            value={returnCondition}
            onValueChange={setReturnCondition}
          >
            <SelectTrigger id="edit_return_condition">
              <SelectValue placeholder="Select condition" />
            </SelectTrigger>
            <SelectContent>
              {WARRANTY_RETURN_CONDITIONS.map((cond) => (
                <SelectItem key={cond.value} value={cond.value}>
                  {cond.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="edit_defect_description">Reported Defect Description *</Label>
          <Textarea
            id="edit_defect_description"
            value={defectDescription}
            onChange={(e) => setDefectDescription(e.target.value)}
            placeholder="Describe the defect or customer complaint..."
            rows={4}
            required
          />
        </div>

        <DialogFooter className="gap-2 sm:gap-0 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={updateMutation.isPending}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={updateMutation.isPending}>
            {updateMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </DialogFooter>
      </form>
    </>
  );
};

EditWarrantyClaimForm.propTypes = {
  claim: PropTypes.object.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  onDone: PropTypes.func,
};

const EditWarrantyClaimModal = ({ open, onOpenChange, claim, onDone }) => {
  if (!claim) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        {open && (
          <EditWarrantyClaimForm
            key={claim.id || "edit-claim"}
            claim={claim}
            onOpenChange={onOpenChange}
            onDone={onDone}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

EditWarrantyClaimModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  claim: PropTypes.object,
  onDone: PropTypes.func,
};

export default EditWarrantyClaimModal;
